// src/Pages/SessionReportDetail/Components/attendance-filters.jsx
import { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Card, Menu, MenuItem } from '@mui/material';
import useTranslation from 'Hooks/useTranslation';

const BORDER = '#E5E7EB';
const TEXT_MUTED = '#8A93A6';
const TEXT_STRONG = '#1F2A3A';
const TEXT_DEFAULT = '#4A5568';

const styles = {
  card: {
    p: 2,
    mb: 2,
    borderRadius: '12px',
    border: `1px solid ${BORDER}`,
    boxShadow: 'none',
    bgcolor: '#fff',
  },
  topRow: { display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    px: 1.5,
    py: '9px',
    border: `1px solid ${BORDER}`,
    borderRadius: '8px',
    flex: '1 1 260px',
    maxWidth: 320,
    bgcolor: '#fff',
  },
  searchInput: {
    border: 'none',
    outline: 'none',
    fontSize: 13,
    flex: 1,
    color: TEXT_STRONG,
    background: 'transparent',
    width: '100%',
    fontFamily: 'inherit',
  },
  dropdownBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 1,
    px: '14px',
    py: '9px',
    borderRadius: '8px',
    border: `1px solid ${BORDER}`,
    bgcolor: '#fff',
    color: TEXT_DEFAULT,
    fontSize: 13,
    fontWeight: 500,
    textTransform: 'none',
    minHeight: 0,
    lineHeight: 1.2,
    '&:hover': { bgcolor: '#fafafa' },
  },
  dropdownLabel: { color: TEXT_MUTED, fontSize: 12.5 },
  dropdownValue: { fontWeight: 600 },
  clearBtn: {
    background: 'none',
    border: 'none',
    color: TEXT_MUTED,
    fontSize: 12.5,
    cursor: 'pointer',
    textDecoration: 'underline',
    padding: 0,
    fontFamily: 'inherit',
  },
  bottomRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.75,
    mt: '14px',
    pt: '14px',
    borderTop: `1px solid ${BORDER}`,
    flexWrap: 'wrap',
  },
  subLabel: { fontSize: 12.5, color: TEXT_MUTED, fontWeight: 500 },
  count: { fontSize: 13, fontWeight: 600, color: TEXT_STRONG },
};

const IconSearch = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke={TEXT_MUTED}
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.5-4.5" />
  </svg>
);

const ChevronDown = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const STATUS_OPTIONS = [
  { value: 'all', labelKey: 'detail.filters.statusAll' },
  { value: 'present', labelKey: 'detail.filters.statusPresent' },
  { value: 'absent', labelKey: 'detail.filters.statusAbsent' },
  { value: 'justified', labelKey: 'detail.filters.statusJustified' },
  { value: 'pending', labelKey: 'detail.filters.statusPending' },
];

/**
 * Filters card for the read-only attendance table. Mirrors SessionsReportFilters.
 *
 * `values.search` updates on every keystroke; the parent hook debounces refetches.
 * `values.status` is one of: 'all' | 'present' | 'absent' | 'justified' | 'pending'.
 */
export default function AttendanceFilters({ values, onChange, shown, total }) {
  const { t2 } = useTranslation('SessionsReport');
  const [statusAnchor, setStatusAnchor] = useState(null);

  const currentStatusLabel =
    STATUS_OPTIONS.find(o => o.value === values.status)?.labelKey || 'detail.filters.statusAll';

  const handleSearch = e => onChange({ ...values, search: e.target.value });
  const handleStatus = status => {
    onChange({ ...values, status });
    setStatusAnchor(null);
  };
  const handleClear = () => onChange({ search: '', status: 'all' });

  return (
    <Card sx={styles.card} elevation={0}>
      <Box sx={styles.topRow}>
        <Box sx={styles.searchBox}>
          <IconSearch />
          <input
            value={values.search}
            placeholder={t2('detail.filters.searchPlaceholder')}
            style={styles.searchInput}
            onChange={handleSearch}
          />
        </Box>

        <Button onClick={e => setStatusAnchor(e.currentTarget)} sx={styles.dropdownBtn} disableRipple disableElevation>
          <Box component="span" sx={styles.dropdownLabel}>
            {t2('detail.columns.status')}:
          </Box>
          <Box component="span" sx={styles.dropdownValue}>
            {t2(currentStatusLabel)}
          </Box>
          <ChevronDown />
        </Button>
        <Menu
          anchorEl={statusAnchor}
          open={!!statusAnchor}
          onClose={() => setStatusAnchor(null)}
          PaperProps={{ sx: { minWidth: 200 } }}
        >
          {STATUS_OPTIONS.map(opt => (
            <MenuItem key={opt.value} selected={opt.value === values.status} onClick={() => handleStatus(opt.value)}>
              {t2(opt.labelKey)}
            </MenuItem>
          ))}
        </Menu>

        <Box sx={{ flex: '1 1 0%' }} />

        <Box component="button" type="button" onClick={handleClear} sx={styles.clearBtn}>
          {t2('detail.clearFilters')}
        </Box>
      </Box>

      <Box sx={styles.bottomRow}>
        <Box component="span" sx={styles.subLabel}>
          {t2('detail.filters.showing')}:
        </Box>
        <Box component="span" sx={styles.count}>
          {t2('detail.filters.showingCount', [String(shown), String(total)])}
        </Box>
      </Box>
    </Card>
  );
}

AttendanceFilters.propTypes = {
  values: PropTypes.shape({ search: PropTypes.string, status: PropTypes.string }).isRequired,
  onChange: PropTypes.func.isRequired,
  shown: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
};
